import React from 'react';
import PropTypes from 'prop-types';
import { arrayMove } from 'react-sortable-hoc';

// Relements
import Button from 'relements/build/esm/ui/button';
import Checkbox from 'relements/build/esm/input/checkbox';
import Accordion from 'relements/build/esm/ui/accordion';
import Box from 'relements/build/esm/ui/box';
import Text from 'relements/build/esm/ui/text';

// Components
import Icon from '@src/components/Icon';
import CreateNewTrigger from './CreateNewTrigger';
import SocialTriggerModal from './SocialTriggerModal';

// Actions
import {
  openComponentModal,
  closeComponentModal,
  createAlert,
  showConfirmationDialog,
  startLoading,
  stopLoading,
} from 'actions/BotActions';
import { AlertTypes } from '@src/constants/MogamboConstants';
import { isProduction } from '@src/utils';
import { userHasPermission } from '@src/actions/_utils';
import { getSDKConfig } from '@src/api/businessV2';

import { PERMISSION_LEVELS } from '@src/const';

// Store
import BotStore from '@src/store';

// Local
import {
  SUPPORTED_VARIABLES,
  SUPPORTED_OPERATORS_BY_TYPE,
} from '../../const/promptTrigger';
import PromptTriggerInfoView from './PromptTriggerInfoView';
import PromptTriggerEditView from './PromptTriggerEditView';

// Local actions
import { transferPromptTriggers } from '../../store/actions';

import styles from './BotPromptTriggerSettings.scss';

const maps = {
  openComponentModal,
  closeComponentModal,
  showConfirmationDialog,
  createAlert,
  startLoading,
  stopLoading,
  transferPromptTriggers,
};

export default class BotPromptTriggerSettings extends React.Component {
  state = {
    activeAccordionItem: -1,
    isSocialPromptModalActive: false,
    isCreateModalActive: false,
    currentSocialPromptValue: null,
    currentIndexOfTrigger: null,
    omniChannelWidgetData: [],
  };

  componentDidMount() {
    // API Call to fetch SDK Config to get data of configured social channels
    const businessId = BotStore.getBusinessesLinkedToBot()?.[0]?.id;
    getSDKConfig(businessId).then(({ success, response }) => {
      if (!success) {
        createAlert(response.error, null, AlertTypes.NEGATIVE);
      } else {
        const WEB_SDK_ID = 5;
        const omniChannelWidgetData =
          response.body?.[WEB_SDK_ID]?.omnichannelWidget;
        this.setState({
          omniChannelWidgetData: omniChannelWidgetData || [],
        });
      }
    });
  }

  render() {
    const { isV2, onPromptTransfer } = this.props;
    const {
      isSocialPromptModalActive,
      currentIndexOfTrigger,
      currentSocialPromptValue,
      isCreateModalActive,
    } = this.state;
    const paddingClass = !isV2 ? styles.triggersPadding : '';
    return (
      <div className={`${styles.triggers} ${paddingClass}`}>
        <div
          className={`${styles.triggersHeader} ${
            isV2 && styles.triggersHeaderAlignEnd
          }`}
        >
          {!isV2 && (
            <div className={styles.triggersHeaderTitle}>
              Bot Prompt Triggers
            </div>
          )}
          {this._isEditable() && (
            <>
              <CreateNewTrigger
                onTextPromptClick={this._showAddNewTriggerModal}
                onSocialPromptClick={() =>
                  this._setIsSocialPromptModalActive(true, true)
                }
                isActive={isCreateModalActive}
                setIsActive={this._setIsCreateModalActive}
              />
              {isSocialPromptModalActive && (
                <SocialTriggerModal
                  isModalActive={isSocialPromptModalActive}
                  setIsModalActive={this._setIsSocialPromptModalActive}
                  setIsCreateModalActive={this._setIsCreateModalActive}
                  totalNumberOfTriggers={this.props.value.length}
                  onChange={(promptData) =>
                    currentIndexOfTrigger !== null
                      ? this._onTriggerDataChange(
                          promptData,
                          currentIndexOfTrigger
                        )
                      : this._onTriggerDataChange(promptData)
                  }
                  data={currentSocialPromptValue}
                  omniChannelWidget={this.state.omniChannelWidgetData}
                />
              )}
            </>
          )}
          {isV2 && (
            <Button
              themed
              className={styles.triggersHeaderTransfer}
              variant="secondary"
              onClick={onPromptTransfer}
              disabled={isProduction()}
            >
              Publish all Prompt Triggers
            </Button>
          )}
        </div>
        {this._renderTriggers()}
      </div>
    );
  }

  /**
   * Sets Modal Status and also, updates the currentIndexOfTrigger and currentSocialPromptValue
   * @param {Boolean} value
   * @param {Boolean} isNew
   */
  _setIsSocialPromptModalActive = (value, isNew = true) => {
    if (isNew) {
      this.setState({
        currentSocialPromptValue: null,
        currentIndexOfTrigger: null,
      });
    }
    this.setState({
      isSocialPromptModalActive: value,
    });
  };

  /**
   * Sets the isCreateModalActive flag of Create Trigger Modal
   * @param {Boolean} value
   */
  _setIsCreateModalActive = (value) => {
    this.setState({
      isCreateModalActive: value,
    });
  };

  _isEditable = () => {
    return (
      !isProduction() &&
      userHasPermission(PERMISSION_LEVELS.BUSINESS_PROMPT_TRIGGER_EDIT_TRANSFER)
    );
  };

  // Will return an array sorted on the basis of priority key
  _sortOnPriority = (list) => {
    return list.sort((x, y) => (x.priority > y.priority ? 1 : -1));
  };

  _renderTriggers = () => {
    if (!this.props.value || this.props.value.length === 0) {
      return (
        <div className={styles.emptyStateWrapper}>
          No Bot Prompt triggers have been configured.
        </div>
      );
    }

    return (
      <Accordion.Sortable
        useDragHandle
        lockAxis="y"
        onSortEnd={this._sortTriggers}
        helperClass="sortableElement"
      >
        <div className={styles.triggerList}>
          {this._renderTriggerListHeader()}
          {this._renderTriggerListBody()}
        </div>
      </Accordion.Sortable>
    );
  };

  _renderTriggerListHeader = () => {
    return (
      <div className={styles.triggerListHeader}>
        <div className={styles.triggerListLeftPad} />
        <div className={styles.headerPriority}>Priority</div>
        <div className={styles.newHeaderTitle}>Trigger Name</div>

        <div className={styles.headerTriggerType}>Trigger Type</div>
        <div className={styles.headerStatus}>Status</div>
        <div className={styles.headerActions}>Actions</div>
        <div className={styles.triggerListRightPad} />
      </div>
    );
  };

  _renderTriggerListBody = () => {
    // Sorting the triggers on the basis of priority
    const sortedTriggerValues = this._sortOnPriority(this.props.value);
    return sortedTriggerValues.map((trigger, index) => {
      const isActive = this.state.activeAccordionItem === index;
      const activeClassName = isActive ? styles.active : '';
      return (
        <Accordion.Item.Sortable
          index={index}
          key={`bot-prompt-trigger-${index}`}
          active={isActive}
          onChange={() => this._toggleAccordion(index)}
          renderHeader={({ ReorderIcon, ExpandIcon }) =>
            this._renderTriggerHeader(index, ReorderIcon, ExpandIcon)
          }
          noPadding
          className={`${styles.triggerListBody} ${activeClassName}`}
        >
          <PromptTriggerInfoView
            {...trigger}
            omniChannelWidget={this.state.omniChannelWidgetData}
          />
        </Accordion.Item.Sortable>
      );
    });
  };

  _renderTriggerHeader = (index, ReorderIcon, ExpandIcon) => {
    const trigger = this.props.value[index];
    return (
      <div className={styles.triggerHeader}>
        <ReorderIcon />
        <div className={styles.headerPriority}>{trigger.priority + 1}</div>
        <div className={styles.newHeaderTitle}>
          <div className={styles.headerTriggerName}>{trigger.name}</div>
          <div className={styles.headerTriggerId}>
            (Trigger-
            {trigger.id || 'New'})
          </div>
        </div>

        <Box
          padding="0"
          className={styles.headerTriggerType}
          data-testid="trigger-type"
        >
          <Box
            className={`${styles.triggerType} ${
              trigger.trigger_type === 0
                ? styles.triggerTypeTextCTA
                : styles.triggerTypeSocial
            }`}
          >
            <Text variant="body.md">
              {trigger.trigger_type === 0 ? 'Text & CTA' : 'Omnichannel'}
            </Text>
          </Box>
        </Box>

        <div className={styles.headerStatus}>
          <Checkbox.Item
            disabled={!this._isEditable()}
            value={trigger.active}
            onChange={(_, e) => this._onChangeTriggerActiveStatus(e, index)}
          >
            Active
          </Checkbox.Item>
        </div>

        <div className={styles.headerActions} data-testid="actions-icons">
          {!this._isEditable() ? null : (
            <>
              <Icon
                containerClassName={styles.actionIcon}
                iconType="edit"
                tooltip="Edit"
                onClick={(e) =>
                  trigger.trigger_type === 1
                    ? this._editSocialBotPromptTrigger(index, e)
                    : this._editBotPromptTrigger(index, e)
                }
              />
              <Icon
                containerClassName={styles.actionIcon}
                iconType="transfer"
                tooltip="Publish"
                onClick={(e) => this._transferTrigger(trigger, e)}
              />
            </>
          )}
          <ExpandIcon />
        </div>
      </div>
    );
  };

  _onChangeTriggerActiveStatus = (e, indexToUpdate) => {
    if (e) e.stopPropagation();
    const triggerData = this.props.value.map((data, index) => {
      if (indexToUpdate === index) return { ...data, active: !data.active };
      return data;
    });

    this.props.onChange(triggerData);
  };

  _showAddNewTriggerModal = () => {
    const defaultVariable = 'page_url';
    const defaultVariableType = SUPPORTED_VARIABLES[defaultVariable].type;

    const newTrigger = {
      name: '',
      active: true,
      rules: {
        clause: 'AND',
        rule_list: [
          {
            variable: defaultVariable,
            operator: SUPPORTED_OPERATORS_BY_TYPE[defaultVariableType][0],
            value: '',
            meta: {},
          },
        ],
      },
      variants: [{}],
      trigger_type: 0,
      priority: this.props.value.length,
    };

    maps.openComponentModal(
      <PromptTriggerEditView
        showConfirmationDialog={maps.showConfirmationDialog}
        closeModal={maps.closeComponentModal}
        {...newTrigger}
        onChange={this._onTriggerDataChange}
      />,
      'New Trigger',
      'Edit Prompt Trigger',
      'CROSS'
    );
  };

  _onTriggerDataChange = (promptData, indexToUpdate) => {
    let triggerData;
    let index = indexToUpdate;
    if (index >= 0) {
      // If index is known => existing prompt data being edited
      triggerData = this.props.value.map((data, i) => {
        return index === i ? promptData : data;
      });
    } else {
      // If index is not known => new prompt data being created
      triggerData = this.props.value.concat(promptData);
      index = triggerData.length - 1;
    }

    this.setState({
      activeAccordionItem: index,
      currentIndexOfTrigger: null,
      currentSocialPromptValue: null,
    });
    this.props.onChange(triggerData);
  };

  _editBotPromptTrigger = (index, e) => {
    if (e) e.stopPropagation();

    maps.openComponentModal(
      <PromptTriggerEditView
        showConfirmationDialog={maps.showConfirmationDialog}
        closeModal={maps.closeComponentModal}
        {...this.props.value[index]}
        onChange={(promptData) => this._onTriggerDataChange(promptData, index)}
      />,
      this.props.value[index].name,
      'Edit Prompt Trigger',
      'CROSS'
    );
  };

  /**
   * When user clicks on Edit Icon of Social Bot Prompt Trigger
   * @param {Number} index
   */
  _editSocialBotPromptTrigger = (index, e) => {
    if (e) e.stopPropagation();
    this.setState(
      {
        currentSocialPromptValue: this.props.value[index],
        currentIndexOfTrigger: index,
      },
      () => this._setIsSocialPromptModalActive(true, false)
    );
  };

  _sortTriggers = ({ oldIndex, newIndex }) => {
    const value = this.props.value.asMutable
      ? this.props.value.asMutable()
      : this.props.value;
    const orderedData = arrayMove(value, oldIndex, newIndex);
    const triggerData = orderedData.map((data, index) => {
      const updateData = { ...data };
      updateData.priority = index;
      return updateData;
    });

    this.props.onChange(triggerData);
  };

  _toggleAccordion = (newIndex) => {
    if (this.state.activeAccordionItem === newIndex) {
      this.setState({
        activeAccordionItem: -1,
      });
    } else {
      this.setState({
        activeAccordionItem: newIndex,
      });
    }
  };

  _canTransferTrigger = () => {
    return !this.props.triggerDataEdited;
  };

  _transferTrigger = (trigger, e) => {
    if (e) e.stopPropagation();
    if (!this._canTransferTrigger()) {
      maps.createAlert(
        'Please save changes before publishing',
        null,
        AlertTypes.NEGATIVE
      );
      return;
    }

    // Show configmr
    maps.showConfirmationDialog({
      okTitle: 'Publish ',
      cancelTitle: 'Cancel',
      title: `Do you want to publish the data of ${trigger.name}(Trigger-${trigger.id})?`,
      message: 'The trigger will be published to Production',
      onOk: async () => {
        maps.startLoading();
        const response = await maps.transferPromptTriggers([trigger.id]);
        if (response) {
          maps.createAlert(
            'Publish complete. The changes will reflect on the Apps within 30 minutes to 1 hour.',
            null,
            AlertTypes.POSITIVE
          );
        } else {
          maps.createAlert(
            'There was an error transferring Tasks.',
            null,
            AlertTypes.NEGATIVE
          );
        }
        maps.stopLoading();
      },
      onCancel: () => {},
    });
  };
}

BotPromptTriggerSettings.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.object,
  triggerDataEdited: PropTypes.bool,
  isV2: PropTypes.bool,
  onPromptTransfer: PropTypes.func,
};

BotPromptTriggerSettings.defaultProps = {
  onChange: () => {},
  value: {},
  triggerDataEdited: false,
  isV2: false,
  onPromptTransfer: () => {},
};
